import classNames from 'classnames'
import throttle from 'lodash.throttle'
import * as React from 'react'

interface Props {
  children: React.ReactElement
  numOfTableRows: number
  className?: string
}

enum ShadowPosition {
  none,
  left,
  right,
  leftAndRight,
}

interface TableScrollShadowProps {
  side: ShadowPosition.left | ShadowPosition.right
  position: { top?: number; left?: number; right?: number }
  height: number
  className?: string
  isVisible: boolean
}

function TableScrollShadow({ className, side, position, height, isVisible }: TableScrollShadowProps) {
  return (
    <div
      className={classNames(
        'pointer-events-none absolute bottom-0 z-20 w-3 border-primary from-gray-900/10 dark:from-gray-900/50',
        className,
        {
          'opacity-100 transition-opacity duration-200': isVisible,
          'opacity-0': !isVisible,
          'border-l bg-gradient-to-r': side === ShadowPosition.left,
          'border-r bg-gradient-to-l': side === ShadowPosition.right,
        }
      )}
      style={{
        ...position,
        height,
      }}
    />
  )
}

const TABLE_SHADOW_MARGIN = 1 // margins to prevent covering borders with the shadow

export function FinancialsTableStickyColumnWrapper({ children, className, numOfTableRows }: Props) {
  const stickyRef = React.useRef<HTMLDivElement>(null)
  const [shadowPosition, setShadowPosition] = React.useState(ShadowPosition.none)
  const [shadowDimensions, setShadowDimensions] = React.useState({ left: 0, top: 0, height: 0 })

  React.useEffect(() => {
    const stickyElement = stickyRef.current
    if (!stickyElement) return

    const onScroll = throttle(function () {
      const stickyElement = stickyRef.current
      if (!stickyElement) return

      const stickyElementScrollLeft = Math.ceil(stickyElement.scrollLeft)

      let newShadowPosition = ShadowPosition.none
      const hasScrollSpaceToLeft = stickyElementScrollLeft > 0
      const hasScrollSpaceToRight =
        Math.ceil(stickyElement.offsetWidth) + stickyElementScrollLeft < stickyElement.scrollWidth
      if (hasScrollSpaceToLeft && hasScrollSpaceToRight) {
        newShadowPosition = ShadowPosition.leftAndRight
      } else if (hasScrollSpaceToLeft) {
        newShadowPosition = ShadowPosition.left
      } else if (hasScrollSpaceToRight) {
        newShadowPosition = ShadowPosition.right
      }

      setShadowPosition(newShadowPosition)
    }, 100)

    onScroll()

    stickyElement.scrollTo({
      left: stickyElement.scrollWidth,
    })

    stickyElement.addEventListener('scroll', onScroll)
    window.addEventListener('resize', onScroll)

    return () => {
      stickyElement.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  React.useEffect(() => {
    const financialsTable = stickyRef.current?.querySelector('table.financials-table')
    const headHeight = financialsTable?.querySelector('thead')?.clientHeight ?? 0
    const firstColumnWidth = financialsTable?.querySelector('tr > td')?.clientWidth ?? 0

    setShadowDimensions({
      left: Math.ceil(firstColumnWidth),
      top: headHeight + TABLE_SHADOW_MARGIN,
      height: (financialsTable?.clientHeight ?? 0) - headHeight - 2 * TABLE_SHADOW_MARGIN,
    })
  }, [numOfTableRows])

  const { radiusCropFillerStyle, shadowLeftStyle, shadowRightStyle } = React.useMemo(
    () => ({
      radiusCropFillerStyle: {
        top: shadowDimensions.top - TABLE_SHADOW_MARGIN,
        height: shadowDimensions.height + 2 * TABLE_SHADOW_MARGIN,
      },
      shadowLeftStyle: {
        top: shadowDimensions.top,
        left: shadowDimensions.left,
      },
      shadowRightStyle: {
        top: shadowDimensions.top,
        right: 0,
      },
    }),
    [shadowDimensions]
  )

  return (
    <div className="financials-table-sticky-wrapper relative max-w-full">
      <div className="radius-crop-filler" style={radiusCropFillerStyle} />
      <TableScrollShadow
        isVisible={[ShadowPosition.left, ShadowPosition.leftAndRight].includes(shadowPosition)}
        side={ShadowPosition.left}
        position={shadowLeftStyle}
        height={shadowDimensions.height}
        className={className}
      />
      <div className="relative max-w-full overflow-auto whitespace-nowrap" ref={stickyRef}>
        {children}
      </div>
      <TableScrollShadow
        isVisible={[ShadowPosition.right, ShadowPosition.leftAndRight].includes(shadowPosition)}
        side={ShadowPosition.right}
        position={shadowRightStyle}
        height={shadowDimensions.height}
        className={className}
      />
    </div>
  )
}
